import React from 'react';

// `locked` blurs + disables the step's controls (header stays sharp) until the previous step is done.
export function SetupStepCard({ step, title, children, isDarkMode, locked }) {
  return (
    <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-4">
        <span className={`text-xs font-bold uppercase tracking-wide ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>Step {step}</span>
        <h3 className={`text-base font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{title}</h3>
        {locked && (
          <span className={`basis-full sm:basis-auto text-xs font-medium italic ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            complete the previous step first
          </span>
        )}
      </div>
      <div
        aria-hidden={locked || undefined}
        className={`transition-[filter,opacity] duration-300 ${locked ? 'blur-[2px] opacity-60 pointer-events-none select-none' : ''}`}
      >
        {children}
      </div>
    </div>
  );
}

// Mobile: compact full-width row; description only unfolds for the selected option.
// alwaysSquare opts a caller out of that (e.g. Voice) and keeps the desktop square-card look at every width.
export function SquareOptionCard({ value, label, Icon, description, selected, onSelect, isDarkMode, alwaysSquare = false }) {
  const toneClass = selected
    ? (isDarkMode ? 'border-blue-400 bg-blue-400/10' : 'border-blue-500 bg-blue-500/5')
    : (isDarkMode ? 'border-slate-700 bg-slate-900/40 hover:border-slate-600' : 'border-slate-200 bg-slate-50 hover:border-slate-300');

  if (alwaysSquare) {
    return (
      <button
        type="button"
        onClick={() => onSelect(value)}
        aria-pressed={selected}
        className={`flex-1 min-w-0 max-w-[220px] aspect-square flex flex-col items-center justify-between text-center rounded-2xl border-2 p-5 transition-colors ${toneClass}`}
      >
        <div className="flex-1 flex flex-col items-center justify-center gap-2">
          <Icon size={36} className={selected ? (isDarkMode ? 'text-blue-400' : 'text-blue-600') : (isDarkMode ? 'text-slate-400' : 'text-slate-500')} />
          <span className={`text-sm font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{label}</span>
        </div>
        {description && (
          <p className={`text-[11px] leading-snug ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{description}</p>
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      aria-pressed={selected}
      className={`w-full sm:flex-1 sm:max-w-[220px] sm:aspect-square rounded-2xl border-2 p-3.5 sm:p-5 text-left sm:text-center transition-colors ${toneClass}`}
    >
      <div className="flex items-center gap-3 sm:flex-1 sm:flex-col sm:items-center sm:justify-center sm:gap-2">
        <Icon size={24} className={`shrink-0 sm:w-9 sm:h-9 ${selected ? (isDarkMode ? 'text-blue-400' : 'text-blue-600') : (isDarkMode ? 'text-slate-400' : 'text-slate-500')}`} />
        <span className={`text-sm font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{label}</span>
      </div>
      {description && (
        <p className={`text-[11px] leading-snug ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} ${selected ? 'mt-2 sm:mt-0 block' : 'hidden sm:block'}`}>
          {description}
        </p>
      )}
    </button>
  );
}
